import * as React from 'react'
import { styled, useTheme } from '@mui/material/styles'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  List,
  ListItem,
  Typography,
  Button,
  useMediaQuery
} from '@mui/material'
import Btn from '../Btn'
// import Loader from '../Loader/Loader'

import check from '../../assets/images/check.svg'
import checkGreen from '../../assets/images/check-green.svg'
import plus from '../../assets/images/plus.svg'
import minus from '../../assets/images/minus.svg'
import { green } from '../../theme/customColors'

import { useDispatch, useSelector } from 'react-redux'
import CancelModal from './CancelModal'
import {
  useDeleteSubscriptionMutation,
  useGetSubscriptionMutation,
  usePatchSubscriptionMutation,
  usePostSubscriptionMutation,
  useGetPricesMutation,
  useGetProductsMutation,
  useGetUserMutation
} from '../../redux/api/userApi'
import BillingDlg from '../BillingDlg'
import EditBillingDlg from '../EditBillingDlg'
import { closeCategories } from '../../redux/features/productCategoriesSlice'
import { type IPlan, type IServicesList } from '../../utils/serviceList'
import { STRIPE_STATUS, STRIPE_CATEGORY } from '../../utils/constants/stripe'
import useToken from '../../hooks/useToken'
import buildServiceList, { comparePrices } from '../../utils/serviceList'
import { type IPrice } from '../../redux/api/types'
import { convertDateToFormattedString } from '../../utils/dateTransfer'
import PopOverCancelForDesctop from './PopOverCancelForDesctop'
import PaymentPastDue from '../PaymentPastDue'
import { isLocalPresenceCountry } from '../../utils/isLocalPresenceCountry'

const AccordionHeading = styled(AccordionSummary)(({ theme }) => ({
  alignItems: 'center',
  borderRadius: '5px',
  overflow: 'hidden',
  position: 'relative',
  '& .MuiAccordionSummary-content': {
    alignItems: 'center',
    borderRadius: '5px',
    display: 'flex',
    justifyContent: 'space-between'
  },
  [theme.breakpoints.down('lg')]: {
    padding: '20px'
  },
  [theme.breakpoints.only('lg')]: {
    padding: '30px'
  },
  [theme.breakpoints.up('xl')]: {
    padding: '40px'
  }
}))
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
const Services: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const dispatch = useDispatch()
  const userState = useSelector((state: any) => state.userState)
  const { prices, products, subscriptions, user } = userState
  const [postSubscription, { isSuccess, isLoading: isLoadingPostSubscribe }] = usePostSubscriptionMutation()
  const [deleteSubscription, { isLoading: isLoadingDeleteSubscription, isSuccess: isSuccessDeleteSubscription }] = useDeleteSubscriptionMutation()
  const [patchSubscription, { isSuccess: isSuccessPatch, isLoading: isLoadingPatch }] = usePatchSubscriptionMutation()
  const { xsrf } = useToken()
  const [getSubscriptions, { isLoading: isLoadingGetSubscribe }] = useGetSubscriptionMutation()
  const [getUser] = useGetUserMutation()

  const [open, setOpen] = React.useState(false)
  const [openSub, setOpenSub] = React.useState(false)
  const [openEditSub, setOpenEditSub] = React.useState(false)
  const [productInfo, setProductInfo] = React.useState({})
  const [priceInfo, setPriceInfo] = React.useState<IPrice[]>([])
  const [priceSubInfo, setPriceSubInfo] = React.useState<any>([])
  const [category, setCategory] = React.useState([])
  const [price, setPrice] = React.useState<any>({})

  const handleOpen = (priceId: any, productId: any, name: string, interval: string, category: any) => {
    setOpen(!open)
    setCategory(category)
    setProductInfo({ priceId, productId, name, interval })
  }

  const handleOpenSub = (price: any, interval: any, priceId: any, productId: any, category: any, name: string) => {
    const initPrices: IPrice[] | undefined = prices.filter((price: IPrice) => price.product === productId)
    initPrices?.sort((a, b) => comparePrices(b, a))
    const initCategory = products.filter((product: any) => product.category === category)
    setOpenSub(!openSub)
    initPrices && setPriceInfo(initPrices)
    setCategory(initCategory)
    setPrice({ priceId, productId, name })
  }

  const handleSubscribe = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    await postSubscription({ productId: price.productId, priceId: price.priceId, token: xsrf })
    await getSubscriptions()
    await getUser()
    setOpenSub(!openSub)
  }

  const handleOpenEditSub = (price: any, interval: any, priceId: any, productId: any, category: any, plans: any, name: string) => {
    const initPrices: IPrice[] | undefined = prices.filter((price: IPrice) => price.product === productId)
    initPrices?.sort((a, b) => comparePrices(b, a))
    const initOldPrice = plans?.find((plan: any) => plan.status === 'active')
    const initCategory = products.filter((product: any) => product.category === category)
    setOpenEditSub(!openEditSub)
    initPrices && setPriceInfo(initPrices)
    setPriceSubInfo(initOldPrice)
    setCategory(initCategory)
    setPrice({ priceId, productId, name })
  }

  const handleEditSubscribe = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    await patchSubscription({ oldPriceId: priceSubInfo.priceId, newPriceId: price.priceId, token: xsrf })
    await getSubscriptions()
    await getUser()
    setOpenEditSub(!openEditSub)
  }

  const isStatusActive = (status?: string) => status === STRIPE_STATUS.ACTIVE
  const isStatusOnGracePeriod = (status?: string) => status === STRIPE_STATUS.ON_GRACE_PERIOD
  const isStatusPastDue = (status?: string) => status === STRIPE_STATUS.PAST_DUE
  const isShowRestoreBtn = (plans: IPlan[], status?: string) => isStatusOnGracePeriod(status) && (!plans.find(el => isStatusActive(el.status)))
  const checkStatus = (plans: IPlan[]) => {
    return plans.some(plan => isStatusActive(plan.status))
  }
  const isShowAfterSubscribeText = (category: string, plans: IPlan[], status?: string) => checkStatus(filteredPlans(category, plans)) && status !== STRIPE_STATUS.ACTIVE

  const restoreSubscribe = async (productId: string, priceId: string, plans: IPlan[]) => {
    const activePlan = plans.find(el => isStatusActive(el.status))
    if (activePlan) {
      await patchSubscription({ oldPriceId: activePlan.priceId, newPriceId: priceId, token: xsrf })
    } else {
      await postSubscription({ productId, priceId, token: xsrf })
    }
    await getSubscriptions()
  }

  const filteredPlans = (category: string, plans: IPlan[]) => {
    if (category === STRIPE_CATEGORY.WEBSITE) {
      const activePlan = plans.find(plan => isStatusActive(plan.status) || isStatusOnGracePeriod(plan.status))
      if (activePlan) return [activePlan]
      return plans
    }
    if (category === STRIPE_CATEGORY.LOCAL_PRESENCE) {
      if (!isLocalPresenceCountry(user)) {
        console.log(isLocalPresenceCountry(user))
        return []
      }
    }
    return plans
  }

  const isWebsiteSubscribe = () => {
    const website = servicesList.find((item: IServicesList) => item.category === STRIPE_CATEGORY.WEBSITE)
    if (website) {
      const hasMatchingStatus = website.plans.some((plan: IPlan) => isStatusOnGracePeriod(plan.status)) || website.plans.every((plan: IPlan) => !plan.status)
      return hasMatchingStatus
    }
    return false
  }

  const categoryState = useSelector((state: any) => state.productCategoriesState)
  const ref = React.useRef<HTMLDivElement>(null)

  const scrollToElement = () => {
    ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }

  React.useEffect(() => {
    const timer = setTimeout(() => {
      scrollToElement()
    }, 200)
    return () => {
      clearTimeout(timer)
    }
  }, [categoryState])

  const subscribeTextAfterOneSub = (plans: IPlan[], rank: string) => {
    let word = ''
    const currentPlans = plans.filter(el => isStatusActive(el.status))
    const currentPlan = plans.find(el => isStatusActive(el.status))
    const maxYourRank = currentPlans.reduce((max, plan) => (plan?.rank && plan.rank > max ? plan.rank : max), currentPlan?.rank ?? 0)
    if (currentPlan?.rank) {
      if (maxYourRank < rank) word = 'Upgrade'
      if (maxYourRank === rank) word = 'Switch'
      if (maxYourRank > rank) word = 'Downgrade'
    }
    return word
  }

  const [getProducts, { isLoading: isLoadingProducts }] = useGetProductsMutation()
  const [getPrices, { isLoading: isLoadingPrices }] = useGetPricesMutation()

  React.useEffect(() => {
    getProducts()
      .then(() => {
      }).catch(() => {})
  }, [])

  React.useEffect(() => {
    getPrices()
      .then(() => {
      }).catch(() => {})
  }, [isLoadingProducts])

  React.useEffect(() => {
    getSubscriptions()
      .then(() => {
      }).catch(() => {})
  }, [isLoadingPrices])

  const servicesList = buildServiceList(products, prices, subscriptions)

  return (
    <>
      <CancelModal open={open} handleOpen={handleOpen} productInfo={productInfo} deleteSubscription={deleteSubscription} isLoading={isLoadingDeleteSubscription} isSuccess={isSuccessDeleteSubscription} category={category}/>
      <BillingDlg open={openSub} handleClose={handleOpenSub} prices={priceInfo} productPrice={price} handleSubmit={handleSubscribe} isSuccess={isSuccess} isLoading={isLoadingPostSubscribe || isLoadingGetSubscribe} category={category}/>
      <EditBillingDlg open={openEditSub} handleClose={handleOpenEditSub} prices={priceInfo} productPrice={price} handleSubmit={handleEditSubscribe} isSuccess={isSuccessPatch} isLoading={isLoadingPatch || isLoadingGetSubscribe} category={category}/>
        {servicesList?.map((item: IServicesList) => {
          const {
            bg,
            iconUrl,
            category,
            title,
            description,
            plans
          } = item
          const [isOpen, setIsOpen] = React.useState<false | true>(false)
          return ((isLocalPresenceCountry(user) || (category !== STRIPE_CATEGORY.LOCAL_PRESENCE)) &&
          <Accordion
            ref = {(categoryState.offerTitle === title) ? ref : null}
            expanded={isOpen || categoryState.offerTitle === title}
            autoFocus={categoryState.offerTitle === title}
            onChange={(e: React.SyntheticEvent, expanded: boolean) => {
              if (categoryState.offerTitle === title) {
                dispatch(closeCategories())
              }
              setIsOpen(expanded)
            }}
            key={title}
            sx={{
              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
              background: 'linear-gradient(196.77deg, rgba(255, 255, 255, 0.85) 0, rgba(255, 255, 255, 0.85) 100%), ' + bg,
              borderRadius: '10px',
              overflow: 'hidden',
              marginBottom: '20px',
              position: 'relative'
            }}
          >
            <AccordionHeading>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'row',
                  [theme.breakpoints.only('lg')]: {
                    width: 'calc(100% - 115px)'
                  }
                }}
              >
                <Box
                  component='img'
                  src={iconUrl}
                  sx={{
                    [theme.breakpoints.down('lg')]: {
                      height: '43px',
                      marginRight: '30px',
                      width: '43px'
                    },
                    [theme.breakpoints.only('lg')]: {
                      height: '52px',
                      marginRight: '15px',
                      width: '52px'
                    },
                    [theme.breakpoints.up('xl')]: {
                      height: '52px',
                      marginRight: '50px',
                      width: '52px'
                    }
                  }}
                />
                <Box>
                  <Typography sx={{ color: theme.palette.grey[40], fontWeight: 500, marginBottom: '5px' }} variant='h4'>{title}</Typography>
                  {isDesktop && (
                    <Typography
                      sx={{
                        color: theme.palette.grey[70],
                        fontWeight: 400,
                        lineHeight: '20px',
                        maxWidth: '484px'
                      }}
                      variant='h5'
                    >
                      {description}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'row'
                }}
              >
                <Box
                  component='img'
                  src={isOpen ? minus : plus}
                  sx={{
                    height: '13px',
                    marginRight: '10px',
                    width: '13px'
                  }}
                />
                <Typography sx={{ color: 'rgba(51, 68, 102, 0.7)', fontSize: '14px', fontWeight: 500 }}>
                  {isOpen ? 'Hide Options' : 'View Options'}
                </Typography>
              </Box>
            </AccordionHeading>
            <AccordionDetails>
              {isMobile && (
                  <Typography sx={{ color: theme.palette.grey[70], fontWeight: 400, lineHeight: '20px', mb: '30px' }} variant='h5'>{description}</Typography>
              )}
              {filteredPlans(category, plans).map((plan: any, index: number) => {
                const { name, price, features, interval, status, priceId, productId, rank, active } = plan
                return (
                  <Box key={index} component='div'>
                    <Box
                      sx={{
                        background: 'white',
                        borderRadius: '5px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        overflow: 'hidden',
                        marginBottom: '15px',
                        position: 'relative',
                        '&:before': {
                          background: bg,
                          content: '""',
                          height: '100%',
                          left: 0,
                          position: 'absolute',
                          rotate: '-180deg',
                          top: 0,
                          width: '8px'
                        },
                        [theme.breakpoints.down('lg')]: {
                          alignItems: 'top',
                          flexDirection: 'column',
                          p: '16px 22px'
                        },
                        [theme.breakpoints.only('lg')]: {
                          alignItems: 'center',
                          p: '20px 40px'
                        },
                        [theme.breakpoints.up('xl')]: {
                          alignItems: 'center',
                          p: '30px 80px'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          [theme.breakpoints.down('lg')]: {
                            alignItems: 'baseline',
                            justifyContent: 'space-between'
                          },
                          [theme.breakpoints.up('lg')]: {
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            [theme.breakpoints.down('lg')]: {
                              alignItems: 'top'
                            },
                            [theme.breakpoints.up('lg')]: {
                              alignItems: 'center'
                            }
                          }}
                        >
                          {isDesktop && (
                              <Box sx={{ mr: '40px', minWidth: '200px', maxWidth: '200px' }}>
                                <Typography
                                    sx={{ color: theme.palette.grey[40] }}
                                    variant='h4'
                                >
                                  {name} {!active ? ' (inactive)' : null}
                                </Typography>
                                <Typography
                                    sx={{
                                      color: theme.palette.grey[40]
                                    }}
                                    variant='h5'
                                >
                                  {isStatusActive(status) || !active
                                    ? null
                                    : <>${(price / 100).toFixed(2)} <span style={{ color: theme.palette.grey[80], fontSize: 10, fontWeight: 500 }}>/{interval}</span> </>
                                  }
                                </Typography>
                              </Box>
                          )}
                          {isMobile && (
                              <Box sx={{ minWidth: '200px', maxWidth: '200px' }}>
                                <Typography
                                    sx={{ color: theme.palette.grey[40] }}
                                    variant='h4'
                                >
                                  {name} {!active ? ' (inactive)' : null}
                                </Typography>
                                <Typography
                                    sx={{
                                      color: theme.palette.grey[40]
                                    }}
                                    variant='h5'
                                >
                                  {isStatusActive(status) || !active
                                    ? null
                                    : <>${(price / 100).toFixed(2)} <span style={{ color: theme.palette.grey[80], fontSize: 10, fontWeight: 500 }}>/{interval}</span> </>
                                  }
                                </Typography>
                              </Box>
                          )}
                          {isDesktop && (
                            <List>
                              {features.map((item: any, index: any) => (
                                <ListItem key={index} sx={{ color: theme.palette.grey[70], fontSize: 14, fontWeight: 400, p: '5px 0' }}>
                                  <Box
                                    component='img'
                                    src={check}
                                    sx={{
                                      height: '9px',
                                      marginRight: '11px',
                                      width: '10px'
                                    }}
                                  />
                                  {item}
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </Box>
                         <Box
                          sx={{
                            display: 'flex',
                            gap: '26px',
                            flexDirection: 'row',
                            [theme.breakpoints.down('lg')]: {
                              alignItems: 'top'
                            },
                            [theme.breakpoints.up('lg')]: {
                              alignItems: 'center'
                            }
                          }}
                        >
                          {(isStatusPastDue(status) &&
                            (
                              <PaymentPastDue color='error.main' flexDirection = 'column' alignItems = 'flex-end' displayIcon = { false }/>
                            )) ||
                            (isStatusActive(status) && !isStatusPastDue(status)
                              ? (
                            <Box
                                sx={{
                                  alignItems: 'center',
                                  display: 'flex',
                                  flexDirection: 'row'
                                }}
                            >
                              <Box
                                  component='img'
                                  src={checkGreen}
                                  sx={{
                                    height: '15px',
                                    marginRight: '8px',
                                    width: '15px'
                                  }}
                              />
                              <Typography sx={{ color: green[900], fontSize: 14, fontWeight: 500, mr: 0 }}>Subscribed</Typography>
                            </Box>)
                              : null
                            )
                          }
                          {(isDesktop && isStatusActive(status) &&
                                  (
                                      <PopOverCancelForDesctop handleOpen={handleOpen} priceId={priceId} productId={productId} name={name} interval={interval} category={category} />
                                  )) ||
                          ((isDesktop && isShowRestoreBtn(plans, status)) &&
                              (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'end' }}>
                                    {<Typography sx={{ color: '#E62E2E' }}>Expiring {convertDateToFormattedString(plan?.endsAt, false)}</Typography>}
                                    <Button disabled={isLoadingPostSubscribe || isLoadingGetSubscribe} onClick={async () => { await restoreSubscribe(productId, priceId, plans) }} sx={{ color: theme.palette.grey[70], fontSize: 14, fontWeight: 500, mr: 0, paddingRight: 0 }}>Restore</Button>
                                  </Box>
                              ))
                          }
                        </Box>
                        {
                            ((isShowAfterSubscribeText(category, plans, status) && !isMobile)
                              ? (
                                        <Btn
                                            onClick={() => { handleOpenEditSub(price, interval, priceId, productId, category, plans, name) }}
                                            variant='outlined'
                                            color='primary'
                                            disabled={!active || (category !== STRIPE_CATEGORY.WEBSITE && isWebsiteSubscribe()) || user?.isPaymentPastDue}
                                            text={`${subscribeTextAfterOneSub(plans, rank)} to ${name as string}`}
                                            sx={{ color: theme.palette.grey[40], minWidth: '120px', p: '22px 11px' }}/>
                                )
                              : null
                            ) ??
                            (((!checkStatus(filteredPlans(category, plans)) && !status && !isMobile)
                              ? (
                                        <Btn
                                            onClick={() => { handleOpenSub(price, interval, priceId, productId, category, name) }}
                                            variant='outlined'
                                            color='primary'
                                            disabled={!active || (category !== STRIPE_CATEGORY.WEBSITE && isWebsiteSubscribe()) || user?.isPaymentPastDue}
                                            text={`Subscribe to ${name as string}`}
                                            sx={{ color: theme.palette.grey[40], minWidth: '120px', p: '22px 11px' }}/>
                                )
                              : null))
                        }
                      </Box>
                      {isMobile && (
                        <List sx={{ p: 0, m: '30px 0 20px' }}>
                          {features.map((item: any, index: any) => (
                            <ListItem key={index} sx={{ color: theme.palette.grey[70], fontSize: 14, fontWeight: 400, p: '5px 0' }}>
                              <Box
                                component='img'
                                src={check}
                                sx={{
                                  height: '9px',
                                  marginRight: '11px',
                                  width: '10px'
                                }}
                              />
                              {item}
                            </ListItem>
                          ))}
                          {isShowRestoreBtn(plans, status) &&
                              <Box>
                                {<Typography sx={{ color: '#E62E2E' }}>Expiring {convertDateToFormattedString(plan?.endsAt, false)}</Typography>}
                                <Button
                              disabled={isLoadingPostSubscribe || isLoadingGetSubscribe}
                              onClick={
                                async () => { await restoreSubscribe(productId, priceId, plans) }}
                              sx={{ color: theme.palette.grey[70], fontSize: 14, fontWeight: 500, mr: 0 }}>
                            Restore
                          </Button>
                              </Box>
                          }
                        </List>
                      )}
                      {((isShowAfterSubscribeText(category, plans, status) && isMobile)
                        ? ((<Btn
                                      onClick={() => { handleOpenEditSub(price, interval, priceId, productId, category, plans, name) }}
                                      variant='outlined'
                                      color='primary'
                                      disabled={category !== STRIPE_CATEGORY.WEBSITE && isWebsiteSubscribe()}
                                      text={`${subscribeTextAfterOneSub(plans, rank)} to ${name as string}`}
                                      sx={{ color: theme.palette.grey[40], width: 'max-content', maxWidth: '-webkit-fill-available', fontSize: '12px', p: '9px 6px', minHeight: '38px' }}/>
                          ))
                        : null) ??
                                  ((checkStatus(filteredPlans(category, plans)) && !status && isMobile)
                                    ? (
                                          <Btn
                                              onClick={() => { handleOpenSub(price, interval, priceId, productId, category, name) }}
                                              variant='outlined'
                                              color='primary'
                                              disabled={(category !== STRIPE_CATEGORY.WEBSITE && isWebsiteSubscribe()) || user?.isPaymentPastDue}
                                              text={`Subscribe to ${name as string}`}
                                              sx={{ color: theme.palette.grey[40], width: 'max-content', maxWidth: '-webkit-fill-available', fontSize: '12px', p: '9px 6px', minHeight: '38px' }}/>
                                      )
                                    : null)
                      }
                      {(isMobile && (isStatusActive(status))) &&
                          (
                          <Button onClick={() => { handleOpen(priceId, productId, name, interval, category) }} sx={{ color: theme.palette.grey[70], justifyContent: 'start', fontSize: 14, fontWeight: 500, mr: 0 }}>Cancel</Button>
                          )
                      }
                    </Box>
                  </Box>
                )
              })}
            </AccordionDetails>
          </Accordion>
          )
        })}
    </>
  )
}

export default Services
